import {
  useMemo,
  useState,
} from 'react';
import type {
  SubmitEvent,
  ChangeEvent,
} from 'react';
import classNames from 'classnames';
import {
  useAlerts,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  UserEntity,
} from '@dinosave/core/types';
import {
  parseError,
} from '@dinosave/core/utils';
import {
  validateEmail,
  validatePassword,
} from '@dinosave/core/validations';
import Button from '@/ui/components/Button';
import Input from '@/ui/components/Input';
import Text from '@/ui/components/Text';
import {
  EyeIcon,
  EyeOffIcon,
  InformationIcon,
} from '@/ui/icons';

import styles from './SignIn.module.css';

type AuthenticatedResponse = {
  user: UserEntity;
  token: string;
}

type SignInProps = {
  kind?: 'default' | 'flat';
  onSuccess?: (response: AuthenticatedResponse) => void;
}

const SignIn = (props: SignInProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    kind = 'default',
    onSuccess,
  } = props;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const emailAuthentication = useAuthentication(s => s.emailAuthentication);

  const addError = useAlerts(s => s.addError);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authenticationError, setAuthenticationError] = useState(false);

  // ##########################################################################
  // MEMOIZATION
  // ##########################################################################

  const isEmailValid = useMemo(() => (
    validateEmail(email)
  ), [email]);

  const isPasswordValid = useMemo(() => (
    validatePassword(password)
  ), [password]);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const togglePassword = () =>  setPasswordVisible(passwordVisible => !passwordVisible);

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmailInvalid(false);
    setAuthenticationError(false);
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordInvalid(false);
    setAuthenticationError(false);
    setPassword(e.target.value);
  };

  const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      loading
    ) return;
    if (
      !isEmailValid
    ) setEmailInvalid(true);
    if (
      !isPasswordValid
    ) setPasswordInvalid(true);

    const isAllValid = isEmailValid && isPasswordValid;

    if (
      isAllValid
    ) {
      setLoading(true);

      try {
        const authResponse = await emailAuthentication(email, password);

        if (
          onSuccess
        ) onSuccess(authResponse);

      } catch (e) {
        setLoading(false);
        const error = parseError(e);

        if (
          error?.status === 401
        ) return setAuthenticationError(true);

        addError(e);
      }
    }
  };

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const signInClassName = classNames({
    [styles.signIn]: true,
    [styles.flat]: kind === 'flat',
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <form
      className={signInClassName}
      onSubmit={handleSubmit}
    >
      <div className={styles.fields}>
        <Text kind="label">
          EMAIL
        </Text>

        <div className={styles.input}>
          <Input
            value={email}
            onChange={handleEmailChange}
            placeholder="Enter your Email"
            invalid={emailInvalid}
            autoFocus
          />
        </div>

        <div className={styles.password}>
          <Text kind="label">
            PASSWORD
          </Text>

          <div className={styles.input}>
            <Input
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter your password"
              type={passwordVisible ? 'text' : 'password'}
              invalid={passwordInvalid}
            />
          </div>

          <div className={styles.passwordReveal}>
            <Button
              color="secondary"
              kind="ghost"
              onClick={togglePassword}
              centered
            >
              {passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
            </Button>
          </div>
        </div>

        {authenticationError && (
          <div className={styles.error}>
            <InformationIcon color="var(--danger)" />

            <Text
              kind="caption"
              size="regular"
              color="danger"
              weight="500"
            >
              Invalid email or password
            </Text>
          </div>
        )}
      </div>

      <div className={styles.submit}>
        <Button
          color="primary"
          type="submit"
          roundness="round"
          disabled={loading}
          loading={loading}
          centered
        >
          <Text
            kind="control"
            size="regular"
          >
            {loading ? 'Just a moment...' : 'Sign In'}
          </Text>
        </Button>
      </div>
    </form>
  );
};

export default SignIn;
